import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Archive, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { agencyAppointmentAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * DeleteAgencyAppointmentDialog — confirms deletion or termination of an
 * agency-level carrier appointment. Active appointments with a writing
 * number are terminated (audit-preserving); the rest are hard-deleted.
 */
export const DeleteAgencyAppointmentDialog = ({ open, onOpenChange, agencyId, appointment, onDeleted }) => {
  const [pending, setPending] = React.useState(false);
  const isTerminate = appointment?.appointment_status === 'Active' && !!appointment?.writing_number;

  const handleConfirm = async () => {
    if (!appointment) return;
    setPending(true);
    try {
      await agencyAppointmentAPI.delete(agencyId, appointment.appointment_id);
      toast.success(isTerminate ? 'Appointment terminated' : 'Appointment deleted');
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete appointment'));
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-agency-appt-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isTerminate ? 'Terminate Appointment' : 'Delete Appointment'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block mb-3">
              Carrier: <strong>{appointment?.carrier_name}</strong><br />
              Status: <strong>{appointment?.appointment_status}</strong>
              {appointment?.writing_number && <><br />Writing #: <strong>{appointment.writing_number}</strong></>}
            </span>
            {isTerminate
              ? <span className="text-amber-600">This active appointment will be terminated to preserve audit history.</span>
              : <span className="text-red-600">This appointment will be deleted. This action cannot be undone.</span>}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={pending}
            className={isTerminate ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-500 hover:bg-red-600'}
            data-testid="confirm-delete-agency-appt"
          >
            {isTerminate
              ? <><Archive size={14} className="mr-1" /> Terminate</>
              : <><Trash2 size={14} className="mr-1" /> Delete</>}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteAgencyAppointmentDialog;
